/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import {
  ReactHookFormRadioGroup,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import ProductFormFieldLayout from './ProductFormFieldLayout';
import { RadioButtonAndLabel } from './RadioButtonAndLabel';
import { useShortTestHelpers } from '../../../hooks/useShortTestHelpers';
import type { FormFieldProps } from '../../../types';
import { useSigmetAirmetTranslation } from '../../../utils/i18n';

const Change: React.FC<FormFieldProps> = ({
  isDisabled,
  isReadOnly,
}: FormFieldProps) => {
  const { t } = useSigmetAirmetTranslation();
  const { isRequired: isRequiredField } = useDraftFormHelpers();
  const { isShortTest } = useShortTestHelpers();
  const isRequired = (value: string): string | boolean =>
    isShortTest() || isRequiredField(value);
  const fieldsDisabled = isDisabled || isShortTest();

  return (
    <ProductFormFieldLayout
      sx={{
        '.MuiButtonBase-root.MuiRadio-root': {
          padding: '6px',
          marginLeft: '3px',
        },
        '.MuiTypography-root.MuiTypography-subtitle1': {
          marginTop: 1,
        },
        marginTop: -1,
      }}
      title={t('change')}
    >
      <ReactHookFormRadioGroup
        disabled={fieldsDisabled}
        isReadOnly={isReadOnly}
        name="change"
        rules={{ validate: { isRequired } }}
      >
        <RadioButtonAndLabel
          data-testid="change-WKN"
          disabled={fieldsDisabled}
          label={t('weakening')}
          value="WKN"
        />
        <RadioButtonAndLabel
          data-testid="change-NC"
          disabled={fieldsDisabled}
          label={t('no-change')}
          value="NC"
        />
        <RadioButtonAndLabel
          data-testid="change-INTSF"
          disabled={fieldsDisabled}
          label={t('intensifying')}
          value="INTSF"
        />
      </ReactHookFormRadioGroup>
    </ProductFormFieldLayout>
  );
};

export default Change;
